import { Stack, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useCallback, useMemo } from 'react';
import { getQuestionLabelAndIcon } from '../../../utils';
import { AdvancedSelect } from '../../../../common/AdvancedSelectA11y/AdvancedSelect';
import { IconOption } from '../../../../common/AdvancedSelectA11y/IconOption';
import type { SurveyQuestion } from '../../../../../domain/Surveys/Questions';
import { QuestionTypes } from '../../../../../domain/Surveys/Questions';
import { BasedOnAnswerSelector } from './BasedOnAnswerSelector';
import { SkippedQuestionSelector } from './SkippedQuestionSelector';
import type { BasedOnAnswer, NextQuestionOption, SkippedNextQuestionOption } from './common';
import { useAnswerLabels, useNextQuestionOption, useQuestionLabel } from './common';

export interface NextQuestionSelectorProps {
  question: SurveyQuestion;
  questions: SurveyQuestion[];
  onChange: (option: NextQuestionOption) => void;
  onSkippedChange: (option: SkippedNextQuestionOption) => void;
}

const StyledStack = styled(Stack)`
  flex-grow: 1;
`;

const TRANSLATION_PREFIX = 'feedback:wizard.section.questions.next-question';

const empty: JSX.Element[] = [];

export const NextQuestionSelector = ({
  question,
  questions,
  onChange,
  onSkippedChange,
}: NextQuestionSelectorProps): JSX.Element => {
  const { t } = useTranslationWithPrefix(TRANSLATION_PREFIX);
  const answerLabels = useAnswerLabels(question);
  const answeredNextQuestion = useNextQuestionOption(questions, question);
  const getQuestionLabel = useQuestionLabel();
  const defaultBasedOnAnswer: BasedOnAnswer = useMemo(
    () => ({
      type: 'based-on-answer',
      options: answerLabels.map(() => ({ type: 'by-order' })),
    }),
    [answerLabels],
  );
  const hasNextBasedOnAnswer =
    question.type === QuestionTypes.RATING_SCALE_5 ||
    question.type === QuestionTypes.RATING_SCALE_7 ||
    question.type === QuestionTypes.SINGLE_CLOSE_ENDED ||
    question.type === QuestionTypes.NET_PROMOTER_SCORE ||
    question.type === QuestionTypes.REACTION;

  const onSelect = useCallback(
    (value: 'by-order' | 'thank-you' | 'based-on' | string) => {
      if (value === 'by-order') {
        onChange({ type: 'by-order' });
        return;
      }
      if (value === 'thank-you') {
        onChange({ type: 'thank-you' });
        return;
      }
      if (value === 'based-on-answer') {
        onChange(defaultBasedOnAnswer);
        return;
      }

      const surveyQuestion = questions.find((q) => q.uuid === value);
      if (surveyQuestion === undefined) return;
      onChange({ type: 'survey-question', question: surveyQuestion });
    },
    [defaultBasedOnAnswer, onChange, questions],
  );

  return (
    <StyledStack direction="column">
      <Text textType="subtitle2">{t('description')}</Text>
      <AdvancedSelect
        id="next-question-selector"
        ariaLabel={t('select-label')}
        selectedValue={
          answeredNextQuestion.type === 'survey-question'
            ? answeredNextQuestion.question.uuid
            : answeredNextQuestion.type
        }
        onSelect={onSelect}
        maxWidth={350}
      >
        {[
          <IconOption id="by-order" value="by-order" key="next">
            {getQuestionLabel(questions, { type: 'by-order' })}
          </IconOption>,
          <IconOption value="thank-you" key="thankYou" id="thankYou">
            {getQuestionLabel(questions, { type: 'thank-you' })}
          </IconOption>,
          ...(hasNextBasedOnAnswer
            ? [
                <IconOption id="based-on-answer" value="based-on-answer" key="based-on-answer">
                  {getQuestionLabel(questions, defaultBasedOnAnswer)}
                </IconOption>,
              ]
            : empty),
          ...questions
            .filter((surveyQuestion) => surveyQuestion.uuid !== question.uuid)
            .map((surveyQuestion) => (
              <IconOption
                id={surveyQuestion.uuid}
                key={surveyQuestion.uuid}
                icon={getQuestionLabelAndIcon(surveyQuestion.type).icon}
                value={surveyQuestion.uuid}
              >
                {getQuestionLabel(questions, { type: 'survey-question', question: surveyQuestion })}
              </IconOption>
            )),
        ]}
      </AdvancedSelect>
      {answeredNextQuestion.type === 'based-on-answer' && (
        <Stack direction="column">
          {hasNextBasedOnAnswer &&
            question.nextByAnswer &&
            answerLabels.map((label, index) => (
              <BasedOnAnswerSelector
                key={label.text}
                basedOnAnswer={answeredNextQuestion}
                question={question}
                questions={questions}
                onChange={onChange}
                label={label}
                index={index}
              />
            ))}
        </Stack>
      )}
      {!question.required && question.type !== QuestionTypes.TITLE_AND_DESCRIPTION && (
        <SkippedQuestionSelector
          onSkippedChange={onSkippedChange}
          question={question}
          questions={questions}
        />
      )}
    </StyledStack>
  );
};
